/**
 * BLUFF CITY FC - ROUTER
 * Complete routing with auth guards and admin routes
 *
 * Admin uses crm-shared AdminShell (mounted via AdminWrapper).
 * AdminShell handles internal tab navigation via hash routing:
 *   /admin#events, /admin#analytics, /admin#signups, /admin#social
 *
 * Native admin pages (roster, games) are separate routes.
 */

import { createRouter, createWebHistory } from 'vue-router'

// Public Views
import Home from '@/views/Home.vue'
import About from '@/views/About.vue'
import Culture from '@/views/Culture.vue'
import FAQ from '@/views/FAQ.vue'
import Donate from '@/views/Donate.vue'
import DonationReceived from '@/views/DonationReceived.vue'
import Contact from '@/views/Contact.vue'
import ContactReceived from '@/views/ContactReceived.vue'
import Waiver from '@/views/Waiver.vue'

// Training Views
import TrainingPage from '@/views/training/TrainingPage.vue'
import TrainingRegister from '@/views/training/TrainingRegister.vue'
import TrainingConfirmation from '@/views/training/TrainingConfirmation.vue'

// Account Views
import Login from '@/views/accounts/Login.vue'
import Signup from '@/views/accounts/Signup.vue'
import Dashboard from '@/views/accounts/Dashboard.vue'
import EditProfile from '@/views/accounts/EditProfile.vue'
import ChangePassword from '@/views/accounts/ChangePassword.vue'
import ForgotPassword from '@/views/accounts/ForgotPassword.vue'
import ResetPassword from '@/views/accounts/ResetPassword.vue'
// League registration (CSAN) — re-enabled 2026-07-10 (pay-per-game Track A).
// See bluff-city-admin/docs/specs/pay-per-game.md requirement ledger.
import LeagueRegistration from '@/views/accounts/LeagueRegistration.vue'
// Magic login — single-use sign-in links from the guest sequence (P3).
import MagicLogin from '@/views/accounts/MagicLogin.vue'

// Admin — crm-shared (single mount, AdminShell handles tabs)
import AdminWrapper from '@/views/admin/AdminWrapper.vue'

// Admin — local views (not yet migrated to crm-shared)
import AdminRoster from '@/views/admin/AdminRoster.vue'
import AdminGames from '@/views/admin/AdminGames.vue'
import AdminGameDetail from '@/views/admin/AdminGameDetail.vue'
import CreateGame from '@/views/admin/CreateGame.vue'

const routes = [
  // ============================================
  // PUBLIC ROUTES
  // ============================================
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { title: 'Bluff City FC — Community Development Through Sport' }
  },
  {
    path: '/about',
    name: 'About',
    component: About,
    meta: { title: 'About - Bluff City FC' }
  },
  {
    path: '/culture',
    name: 'Culture',
    component: Culture,
    meta: { title: 'Culture - Bluff City FC' }
  },
  {
    path: '/faq',
    name: 'FAQ',
    component: FAQ,
    meta: { title: 'FAQ - Bluff City FC' }
  },
  {
    path: '/donate',
    name: 'Donate',
    component: Donate,
    meta: { title: 'Donate - Bluff City FC' }
  },
  {
    path: '/donation-received',
    name: 'DonationReceived',
    component: DonationReceived,
    meta: { title: 'Thank You - Bluff City FC' }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
    meta: { title: 'Contact - Bluff City FC' }
  },
  {
    path: '/contact-received',
    name: 'ContactReceived',
    component: ContactReceived,
    meta: { title: 'Message Sent - Bluff City FC' }
  },
  {
    path: '/waiver',
    name: 'Waiver',
    component: Waiver,
    meta: { title: 'Waiver - Bluff City FC' }
  },

  // ============================================
  // TRAINING ROUTES
  // ============================================
  {
    path: '/training',
    name: 'Training',
    component: TrainingPage,
    meta: { title: 'Open Training - Bluff City FC' }
  },
  {
    path: '/training/register',
    name: 'TrainingRegister',
    component: TrainingRegister,
    meta: { title: 'Register - Bluff City FC' }
  },
  {
    path: '/training/confirmation',
    name: 'TrainingConfirmation',
    component: TrainingConfirmation,
    meta: { title: 'Registration Complete - Bluff City FC' }
  },

  // Legacy tryout redirects (preserve old links/ads/bookmarks)
  { path: '/tryouts', redirect: '/training' },
  { path: '/tryouts/register', redirect: '/training/register' },
  { path: '/tryouts/confirmation', redirect: '/training/confirmation' },

  // ============================================
  // ACCOUNT ROUTES
  // ============================================
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { title: 'Login - Bluff City FC', guest: true }
  },
  {
    path: '/signup',
    name: 'Signup',
    component: Signup,
    meta: { title: 'Sign Up - Bluff City FC', guest: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { title: 'Dashboard - Bluff City FC', requiresAuth: true }
  },
  {
    path: '/profile/edit',
    name: 'EditProfile',
    component: EditProfile,
    meta: { title: 'Edit Profile - Bluff City FC', requiresAuth: true }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePassword,
    meta: { title: 'Change Password - Bluff City FC', requiresAuth: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { title: 'Forgot Password - Bluff City FC', guest: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { title: 'Reset Password - Bluff City FC', guest: true }
  },
  {
    path: '/league-registration',
    name: 'LeagueRegistration',
    component: LeagueRegistration,
    meta: { title: 'Guest Player Registration - Bluff City FC', requiresAuth: true }
  },
  // Availability + Game Day. Both are opened straight from an email with no
  // login — the per-ask token in the URL is the auth. These used to be hosted
  // as Supabase edge functions, which serve every response as text/plain, so
  // players saw raw HTML source (coach 2026-07-30). They belong on the site.
  {
    path: '/availability',
    name: 'Availability',
    component: () => import('@/views/accounts/Availability.vue'),
    meta: { title: 'Are you in? - Bluff City FC' }
  },
  {
    path: '/gameday',
    name: 'GameDay',
    component: () => import('@/views/GameDay.vue'),
    meta: { title: 'Game Day - Bluff City FC' }
  },
  {
    path: '/magic/:token',
    name: 'MagicLogin',
    component: MagicLogin,
    // NOT guest-only: the guard bounces guest routes to Dashboard when a
    // session exists, which meant an already-logged-in player clicking their
    // registration link landed on their profile instead of the form
    // (coach 2026-07-30). This route swaps the session for the token's user
    // and forwards to ?redirect, so it must run in both states.
    meta: { title: 'Signing In - Bluff City FC' }
  },

  // ============================================
  // ADMIN — CRM-SHARED (AdminShell via single mount)
  // AdminShell handles: Events, Registrations, Analytics, Social
  // Tab navigation via hash: /admin#events, /admin#analytics, etc.
  // ============================================
  {
    path: '/admin',
    name: 'Admin',
    component: AdminWrapper,
    meta: { title: 'Admin - Bluff City FC', requiresAuth: true, requiresAdmin: true }
  },

  // Redirects for old individual routes → AdminShell with hash
  { path: '/admin/analytics', redirect: '/admin#analytics' },
  { path: '/admin/funnel', redirect: '/admin#analytics' },
  { path: '/admin/events', redirect: '/admin#events' },
  { path: '/admin/tryouts/:eventId', redirect: '/admin#signups' },

  // ============================================
  // ADMIN — LOCAL (native BCFC views, not in crm-shared)
  // ============================================
  {
    path: '/admin/roster',
    name: 'AdminRoster',
    component: AdminRoster,
    meta: { title: 'Roster - Bluff City FC', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/games',
    name: 'AdminGames',
    component: AdminGames,
    meta: { title: 'Games - Bluff City FC', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/games/create',
    name: 'CreateGame',
    component: CreateGame,
    meta: { title: 'Create Game - Bluff City FC', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/games/:id',
    name: 'AdminGameDetail',
    component: AdminGameDetail,
    meta: { title: 'Game Detail - Bluff City FC', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/PrivacyPolicy.vue')
  },

  // ============================================
  // PLAYBOOK ROUTES
  // ============================================
  {
    path: '/playbook',
    name: 'PlaybookList',
    component: () => import('@/views/playbook/PlaybookList.vue'),
    meta: { title: 'Game Plans - Bluff City FC', requiresAuth: true }
  },
  {
    path: '/playbook/invite/:token',
    name: 'PlaybookInvite',
    component: () => import('@/views/playbook/PlaybookInvite.vue'),
    meta: { title: 'Join the squad - Bluff City FC' }
  },
  {
    path: '/playbook/:playbookId',
    name: 'PlaybookViewer',
    component: () => import('@/views/playbook/PlaybookViewer.vue'),
    meta: { title: 'Game Plan - Bluff City FC', requiresAuth: true }
  },

  // ============================================
  // CATCH ALL
  // ============================================
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // Update page title
  document.title = to.meta.title || 'Bluff City FC'

  const token = localStorage.getItem('token')
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const isLoggedIn = !!token
  const isAdmin = user.role === 'admin'

  // Redirect to login if route requires auth
  if (to.meta.requiresAuth && !isLoggedIn) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  // Redirect to dashboard if route requires admin but user isn't admin
  if (to.meta.requiresAdmin && !isAdmin) {
    next({ name: 'Dashboard' })
    return
  }

  // Already logged in and visiting a guest-only page (login/signup/reset).
  // If that page was reached with a ?redirect target — e.g. an expired magic
  // link offering "Log in instead" — honour the target rather than dumping the
  // player on their dashboard, which reads as "the link did nothing"
  // (coach 2026-07-30).
  if (to.meta.guest && isLoggedIn) {
    const target = typeof to.query.redirect === 'string' ? to.query.redirect : null
    next(target && target.startsWith('/') && !target.startsWith('//') ? target : { name: 'Dashboard' })
    return
  }

  next()
})

export default router